import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { CloudArrowUpIcon, NoSymbolIcon, XMarkIcon, DocumentIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

const FileReceiver = ({
  title,
  onFilesChange,
  maxFiles = 1,
  disabled = false,
  type = 'pdf',
  // New props for enhanced version
  currentFile = null, // Pass the current file object to show it
  onRemoveFile = null, // Callback when user clicks X to remove file
  showFileName = false, // Enable the new enhanced view with file name display
  tooltip = null, // Optional tooltip text shown on hover of ? icon
}) => {
  const onDrop = useCallback((droppedFiles) => {
    console.log('FileReceiver - onDrop called:', { 
      droppedFiles: droppedFiles.length, 
      disabled, 
      maxFiles 
    });
    
    if (disabled) {
      console.log('FileReceiver - Drop ignored: disabled');
      return;
    }
    
    // Since we don't maintain local state, just pass the first file
    const filesToAdd = droppedFiles.slice(0, maxFiles);
    
    console.log('FileReceiver - Processing files:', { 
      filesToAdd: filesToAdd.length,
      fileNames: filesToAdd.map(f => f.name)
    });
    
    if (filesToAdd.length > 0) {
      console.log('FileReceiver - Calling onFilesChange with new files');
      onFilesChange?.(filesToAdd);
    } else {
      console.log('FileReceiver - No files to add');
    }
  }, [maxFiles, disabled, onFilesChange]);



  const getAcceptedFileTypes = () => {
    switch (type) {
      case 'excel':
        return {
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          'application/vnd.ms-excel': ['.xls']
        };
      case 'xml':
        return {
          'application/xml': ['.xml'],
          'text/xml': ['.xml']
        };
      case 'image-or-pdf':
        return {
          'application/pdf': ['.pdf'],
          'image/jpeg': ['.jpg', '.jpeg'],
          'image/png': ['.png'],
          'image/gif': ['.gif'],
          'image/webp': ['.webp'],
          'image/bmp': ['.bmp'],
          'image/tiff': ['.tif', '.tiff'],
          'image/heic': ['.heic'],
          'image/heif': ['.heif']
        };
      case 'pdf':
      default:
        return {
          'application/pdf': ['.pdf']
        };
    }
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    maxFiles: maxFiles,
    accept: getAcceptedFileTypes(),
    disabled,
    onDragEnter: () => console.log('FileReceiver - Drag enter'),
    onDragLeave: () => console.log('FileReceiver - Drag leave'),
    onDropAccepted: (files) => console.log('FileReceiver - Drop accepted:', files.length),
    onDropRejected: (rejectedFiles) => console.log('FileReceiver - Drop rejected:', rejectedFiles)
  });

  const getFileTypeLabel = () => {
    switch (type) {
      case 'excel':
        return 'Excel (.xlsx, .xls)';
      case 'xml':
        return 'XML (.xml)';
      case 'image-or-pdf':
        return 'PDF o Imagen (.pdf, .jpg, .png, etc.)';
      case 'pdf':
      default:
        return 'PDF (.pdf)';
    }
  };

  return (
    <div className="h-full">
      <div className="mb-2">
        <div className="font-medium text-sm flex items-center gap-1" title={title}>
          {title}
          {tooltip && (
            <div className="relative group">
              <QuestionMarkCircleIcon className="w-5 h-5 text-blue-500 hover:text-blue-700 cursor-help" />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-64 z-50 shadow-lg">
                {tooltip}
                <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-800"></div>
              </div>
            </div>
          )}
        </div>
        <span className="text-xs text-gray-500">
          Hasta <b>{maxFiles}</b> archivos • {getFileTypeLabel()}
        </span>
      </div>

      {/* Enhanced view: Show file name with delete button */}
      {showFileName && currentFile ? (
        <div className="border-2 border-green-400 bg-green-50 rounded-lg p-3 h-[120px] flex items-center justify-center">
          <div className="flex items-center gap-3 w-full">
            <DocumentIcon className="w-8 h-8 text-green-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-green-800 truncate" title={currentFile.name}>
                {currentFile.name}
              </div>
              <div className="text-xs text-green-600 mt-1">
                Archivo cargado
              </div>
            </div>
            {onRemoveFile && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFile();
                }}
                className="flex-shrink-0 p-1.5 rounded-full hover:bg-red-100 transition-colors group"
                title="Eliminar archivo"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500 group-hover:text-red-600" />
              </button>
            )}
          </div>
        </div>
      ) : (
        /* Original view: Dropzone */
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-3 transition-all duration-200 h-[120px] overflow-y-auto
            ${disabled
              ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
              : isDragReject
              ? 'border-red-400 bg-red-50 ring-2 ring-red-200'
              : isDragActive
              ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-200 scale-105'
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50 cursor-pointer'
            }`}
        >
          <input {...getInputProps()} />
          <div className="text-center h-full flex flex-col items-center justify-center">
            <div className="space-y-2">
              {disabled ? (
                <NoSymbolIcon
                  className={`w-8 h-8 mx-auto text-gray-400`}
                />
              ) : isDragReject ? (
                <NoSymbolIcon
                  className={`w-8 h-8 mx-auto text-red-500 animate-pulse`}
                />
              ) : (
                <CloudArrowUpIcon
                  className={`w-8 h-8 mx-auto transition-colors duration-200 ${
                    isDragActive ? 'text-blue-500 animate-bounce' : 'text-gray-400'
                  }`}
                />
              )}
              <div className={`text-xs transition-colors duration-200 ${
                disabled ? 'text-gray-500' :
                isDragReject ? 'text-red-600' :
                isDragActive ? 'text-blue-600 font-medium' : 'text-gray-500'
              }`}>
                {disabled
                  ? "Se ha llegado al máximo de archivos"
                  : isDragReject
                  ? "Tipo de archivo no válido"
                  : isDragActive
                  ? "¡Suelta el archivo aquí!"
                  : `Arrastra o selecciona ${getFileTypeLabel()}`
                }
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileReceiver; 